import { useId } from 'preact/hooks';
import { ACCENT, ACCENT_BRIGHT, ACCENT_HI, ACCENT_LO, ACCENT_DEEP } from '../../lib/medievalTheme';

// World-tree frame around the profile avatar: the Yggdrasil drawing
// from the Dolmenwood frame set, carved in the same dark stone as the
// body panels. Same recipe as TrophyBadge: the drawing is an alpha
// mask over a gradient, a lighting filter turns the blurred alpha into
// relief so trunk, twigs and root knot catch one top-left light, then
// hammered grain and patina age the surface. A blurred dark copy
// underneath grounds it on the hero art.
//
// Colour comes from the page accent tokens mixed into the panel
// stone, so the tree recolours with the player's backdrop exactly as
// the panel rims do. The specular highlight is tinted the same way;
// white specular is what made an earlier pass read as silver.

const TREE_HREF = '/ornaments/profile-tree-frame.png';
// Mask image dimensions. The viewBox matches so the lighting filter's
// blur radius and the grain frequency are stable in image pixels.
export const TREE_W = 900;
export const TREE_H = 948;

// Panel stone (PANEL_BG is rgb 12,10,8) lifted toward the accent.
const STONE = '#0c0a08';
const mix = (accent, pct) => `color-mix(in srgb, ${accent} 1) ${pct}%, ${STONE})`;
const PALETTE = {
  hi: mix(ACCENT_BRIGHT, 48),
  light: mix(ACCENT, 36),
  mid: mix(ACCENT_LO, 22),
  dark: mix(ACCENT_DEEP, 10),
  deep: '#050403',
};

export default function TreeFrame({ tier, style = {} }) {
  const id = `tf${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const u = (name) => `url(#${id}-${name})`;
  const strong = tier === 'master' || tier === 'grandmaster';
  const metal = PALETTE;

  return (
    <div
      aria-hidden="true"
      className="absolute pointer-events-none"
      style={{
        aspectRatio: `${TREE_W} / ${TREE_H}`,
        filter: `drop-shadow(0 0 ${strong ? '0.9cqw' : '0.45cqw'} ${ACCENT} ${strong ? 0.35 : 0.18}))`,
        ...style,
      }}
    >
      <svg viewBox={`0 0 ${TREE_W} ${TREE_H}`} className="absolute inset-0 w-full h-full">
        <defs>
          <linearGradient id={`${id}-metal`} x1="0" y1="0" x2="0.35" y2="1">
            <stop offset="0" stop-color={metal.hi} />
            <stop offset="0.28" stop-color={metal.light} />
            <stop offset="0.55" stop-color={metal.mid} />
            <stop offset="0.8" stop-color={metal.dark} />
            <stop offset="1" stop-color={metal.mid} />
          </linearGradient>
          <mask id={`${id}-tree`} maskUnits="userSpaceOnUse" x="0" y="0" width={TREE_W} height={TREE_H} style={{ maskType: 'alpha' }}>
            <image href={TREE_HREF} x="0" y="0" width={TREE_W} height={TREE_H} preserveAspectRatio="none" />
          </mask>
          {/* Relief lighting. Blurred alpha is the height map; diffuse
              shades the slopes, specular adds the hot edge highlight. */}
          <filter id={`${id}-light`} x="-5%" y="-5%" width="110%" height="110%" color-interpolation-filters="sRGB">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3" result="bump" />
            <feDiffuseLighting in="bump" surfaceScale="6" diffuseConstant="1.25" lighting-color="#ffffff" result="diffuse">
              <feDistantLight azimuth="225" elevation="50" />
            </feDiffuseLighting>
            <feComposite in="diffuse" in2="SourceGraphic" operator="arithmetic" k1="1" k2="0" k3="0" k4="0" result="shaded" />
            <feSpecularLighting in="bump" surfaceScale="6" specularConstant="0.45" specularExponent="18" lighting-color={`${ACCENT_HI} 1)`} result="spec">
              <feDistantLight azimuth="225" elevation="42" />
            </feSpecularLighting>
            <feComposite in="spec" in2="SourceAlpha" operator="in" result="specIn" />
            <feComposite in="shaded" in2="specIn" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="lit" />
            <feComposite in="lit" in2="SourceAlpha" operator="in" />
          </filter>
          <filter id={`${id}-hammer`} x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.16" numOctaves="3" seed="7" result="noise" />
            <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 1.4 -0.35" />
          </filter>
          <filter id={`${id}-patina`} x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.014 0.022" numOctaves="4" seed="3" result="noise" />
            <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 2.2 -0.9" />
          </filter>
          <filter id={`${id}-shadow`} x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="5" />
          </filter>
        </defs>

        {/* Cast shadow onto the art. */}
        <rect x="0" y="0" width={TREE_W} height={TREE_H} fill="#000000" opacity="0.75" mask={u('tree')} transform="translate(6 12)" filter={u('shadow')} />
        {/* Lit metal, then wear. */}
        <g filter={u('light')}>
          <rect x="0" y="0" width={TREE_W} height={TREE_H} fill={u('metal')} mask={u('tree')} />
        </g>
        <g mask={u('tree')}>
          <rect x="0" y="0" width={TREE_W} height={TREE_H} filter={u('hammer')} opacity="0.4" style={{ mixBlendMode: 'overlay' }} />
          <rect x="0" y="0" width={TREE_W} height={TREE_H} filter={u('patina')} fill={metal.deep} opacity="0.4" style={{ mixBlendMode: 'multiply' }} />
        </g>
      </svg>
    </div>
  );
}
